import {
    Component,
    OnDestroy,
    NgZone,
    ViewChild,
    ElementRef,
    AfterViewInit,
    AfterViewChecked,
    HostListener
} from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, RouterLink } from '@angular/router';

import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { CustomerService } from '@core/services/customer/customer.service';
import { OutletService } from '@core/services/outlet/outlet.service';
import { UtilityService } from '@core/services/util/utility.service';
import { LoginService } from '@core/services/login/login.service';
import { SessionStorageService } from '@core/services/util/session-storage.service';
import { ToastrService } from '@core/services/notification/toastr.service';
import { AuthService } from '@core/services/auth/auth.service';
import { GoogleMapService } from '@core/services/google_map_service/google-map.service';
import { LocationService } from '@core/services/location/location.service';
import { CartNotificationService } from '@core/services/cart_notification/cart-notification.service';
import { LocationApiService } from '@core/services/google_map_service/location-api.service';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { StorageService } from '@core/services/util/storage.service';

import { BaseComponent } from '@core/components/base/base.component';

import { DesktopLoginWrapperComponent } from '@modules/authentication/components/desktop-components/desktop-login-wrapper/desktop-login-wrapper.component';
import { MobileLoginWrapperComponent } from '@modules/authentication/components/mobile-components/mobile-login-wrapper/mobile-login-wrapper.component';
import { CrossBrandOrderResetComponent } from '@core/components/header/components/cross-brand-order-reset/cross-brand-order-reset.component';

import { CartService } from '@core/services/cart/cart.service';
import { SEOService } from '@core/services/seo/seo.service';

import { OutletType } from '@shared/models/outlet-type';
import { LatLng, HeaderState } from '@app/shared/models/util';
import { AppConfig } from '@project_app/config/app.config';
import { FilterService } from '@core/services/filter/filter.service';
import { CustomerApiService } from '@app/core/http/customer-api.service';
import { PageNavigationService } from '@app/core/services/page-navigation/page-navigation.service';
import { AddressService } from '@app/core/services/address/address.service';
import { CustomerAddress } from '@app/shared/models/interfaces/customer-address';
import { ReverseGeoCodeResponse } from '@app/shared/models/api-response/reverse-geocode-response';
import { GtmService } from '@app/core/services/analytics/gtm.service';
import { environment } from '@environment';
import { PslaService } from '@app/core/services/psla/psla.service';
import { CartDropdownComponent } from './components/cart-dropdown/cart-dropdown.component';
import { MatMenuModule } from '@angular/material/menu';
import { SpriteIconsComponent } from '@shared/components/sprite-icons/sprite-icons.component';
import { GooglePlaceDirective } from '@shared/directives/google-place.directive';
import { NgStyle, NgTemplateOutlet, SlicePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PopoverComponent } from '@shared/components/popover/popover.component';
import { MatIconModule } from '@angular/material/icon';
import { SmartBannerComponent } from './components/smart-banner/smart-banner.component';
import { HeaderLoaderComponent } from './components/header-loader/header-loader.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: true,
    imports: [
        HeaderLoaderComponent,
        SmartBannerComponent,
        MatIconModule,
        PopoverComponent,
        RouterLink,
        FormsModule,
        NgStyle,
        NgTemplateOutlet,
        GooglePlaceDirective,
        SpriteIconsComponent,
        MatMenuModule,
        CartDropdownComponent,
        SlicePipe
    ]
})
export class HeaderComponent extends BaseComponent implements OnDestroy, AfterViewInit, AfterViewChecked {
    private readonly SCROLL_THRESHOLD_HEIGHT = 70;
    customerName: string;
    locality: string;
    address_type: string;
    addresses: CustomerAddress[];
    locationChangeInfo = {
        show: false,
        message: ''
    };
    showLocationChange = false;
    locating: boolean;
    position = {};
    destroySubject$: Subject<void> = new Subject();

    headerState: HeaderState;
    placeSearch = ''; // outlet search input ngModel
    locationNotProper = false;

    cartCount = 0;
    smartBannerHeight = 70;

    isEatclub = false;
    is_veg = false;
    is_logged_in = false;

    show_smart_banner = false;
    checkForSmartBanner = false;
    fetchBalance = true;

    showMobileHeader: boolean;
    showCartDropDown: boolean;

    creditBal = null;
    headerNotification = {};
    outletLocality;

    isScrolled = false;
    backgroundColor: string;
    textColor: string;
    boxShadow: string;
    dynamicStyles = {
        initialStyle: {
            backgroundColor: 'rgba(255, 255, 255, 0)',
            boxShadow: 'none',
            textColor: '#fff'
        },
        finalStyle: {
            backgroundColor: 'rgba(255, 255, 255, 1)',
            boxShadow: '0 3px 6px 0 rgb(0 0 0 / 10%)',
            textColor: '#000'
        }
    };

    @ViewChild('cartRef') cartRef: ElementRef;
    @ViewChild('headerElement') headerElement: ElementRef<HeaderComponent>;
    @ViewChild('headerSpacing') headerSpacing: ElementRef;
    private scrollThrottleTimer: number | null = null;

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private zone: NgZone,
        private dialog: MatDialog,
        private bottomSheet: MatBottomSheet,
        private loginService: LoginService,
        private customerApiService: CustomerApiService,
        private outletService: OutletService,
        private sessionStorage: SessionStorageService,
        private utilityService: UtilityService,
        private toastrService: ToastrService,
        private googleMapService: GoogleMapService,
        private authService: AuthService,
        private locationService: LocationService,
        private cartNotificationService: CartNotificationService,
        private locationApiService: LocationApiService,
        private customerService: CustomerService,
        private authenticationService: AuthenticationService,
        private cartService: CartService,
        private seo: SEOService,
        private storageService: StorageService,
        private filterService: FilterService,
        private pageNavigationService: PageNavigationService,
        private addressService: AddressService,
        private gtmService: GtmService,
        private pslaService: PslaService
    ) {
        super();

        this.utilityService.currentHeaderState.subscribe(data => {
            this.headerState = data;
        });

        this.authService.init();

        this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: any) => {
            const routeData = this._extractRouteData(this.activatedRoute);
            const urlWithoutParam = event.url.split(/[?#]/)[0];

            this.updateHeaderState(urlWithoutParam, event.url, routeData);
            this.setInitialHeaderStyles();
            this.handlePostNavigationActions(urlWithoutParam);
        });

        this.isEatclub = environment.brand.id === 19;
        this.isLoggedIn();
    }

    @HostListener('window:scroll', [])
    onWindowScroll(): void {
        if (this.scrollThrottleTimer) {
            return;
        }

        this.scrollThrottleTimer = window.setTimeout(() => {
            if (!this.shouldApplyScrollEffects()) {
                this.scrollThrottleTimer = null;
                return;
            }

            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const scrollPercentage = this.calculateScrollPercentage(scrollTop, this.SCROLL_THRESHOLD_HEIGHT);
            this.updateHeaderStyles(scrollPercentage);
            this.scrollThrottleTimer = null;
        }, 100);
    }

    fetchCreditBalance() {
        const shouldFetchBalance =
            this.headerState.isMainPage ||
            this.headerState.isMenuPage ||
            this.headerState.isOffersPage ||
            this.headerState.isAccountPage ||
            !this.isMobile;

        if (shouldFetchBalance && this.is_logged_in && this.fetchBalance) {
            this.fetchBalance = false;
            this.customerApiService.fetchCreditBalance();
        }
    }

    checkSmartBanner() {
        const already_shown = this.storageService.get('smart_banner_shown');
        const isSafari = this.platformService.isSafariBrowser();
        this.show_smart_banner =
            this.headerState.isMainPage && this.isMobile && !already_shown && !isSafari && AppConfig.isSmartBanner;
        this.checkForSmartBanner = true;
    }

    ngOnInitBrowser() {
        this.pslaService.pslaNotification.subscribe(data => {
            if (this.headerState.isMainPage) {
                this.handlePslaNotification(data);
            }
        });

        this.addressService.setLocality();
        this.addressService.outletLocality.subscribe(
            (outletLocality: { locality: string; address_type: string; address_name: string }) => {
                this.outletLocality = outletLocality;
            }
        );

        this.addressService.addresses.subscribe((addresses: CustomerAddress[]) => {
            this.addresses = addresses;
        });

        this.activatedRoute.queryParams.subscribe(queryParams => {
            if (queryParams.mode && queryParams.mode === 'authentication' && !this.isLoggedIn()) {
                this.showSignInModal();
            }
        });

        this.addressService.addresses.subscribe((addresses: CustomerAddress[]) => {
            this.addresses = addresses;
        });

        this.cartService.crossBrandReset.subscribe(data => {
            if (Object.keys(data).length) {
                this.dialog.open(CrossBrandOrderResetComponent, { maxWidth: 450, data });
            }
        });

        this.cartService.state.subscribe((cart: any) => {
            const items = cart && cart.items;
            this.cartCount = Object.values(items).reduce((acc: number, item: any) => acc + item.quantity, 0) as number;
            if (this.headerState.isMenuPage && !this.isMobile && this.cartCount && this.cartRef) {
                this.cartNotificationService.open();
            }
        });

        this.filterService.isVeg$.subscribe((is_veg: boolean) => {
            this.is_veg = is_veg;
        });

        this.loginService.userLoginSuccess.subscribe(() => {
            this.isLoggedIn();
            this.fetchCreditBalance();
        });

        this.loginService.userLogoutSuccess.subscribe(() => {
            this.fetchBalance = true;
            this.isLoggedIn();
        });

        this.checkCurrentLocation();

        this.customerApiService.creditBal.subscribe((balance: number) => {
            this.creditBal = balance;
        });
    }

    toggleOnlyVeg() {
        this.filterService.toggleVeg(!this.is_veg);
    }

    getSmartBannerExpiryDate() {
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 1);
        return expiryDate;
    }

    ngAfterViewInitBrowser() {
        this.checkSmartBanner();
        this.cartNotificationService.setElementReference(this.cartRef);
    }

    ngAfterViewCheckedBrowser() {
        if (this.checkForSmartBanner) {
            setTimeout(() => {
                this._adjustHeaderSpacing();
                this._adjustHeaderPosition();
                this.checkForSmartBanner = false;
            });
        }
    }

    ngOnDestroy() {
        this.updateHeaderSearchBoxStatus(false);

        if (this.scrollThrottleTimer) {
            window.clearTimeout(this.scrollThrottleTimer);
            this.scrollThrottleTimer = null;
        }
    }

    private _adjustHeaderPosition() {
        const headerElement = this.headerElement.nativeElement as any;
        const pos = this.show_smart_banner ? this.smartBannerHeight : 0;
        headerElement.setAttribute('style', `top: ${pos}px`);
    }

    private _adjustHeaderSpacing() {
        const headerElement = this.headerElement.nativeElement as any;
        const smartBannerElement = this.show_smart_banner ? this.smartBannerHeight : 0;
        const space = Number(headerElement.offsetHeight) + smartBannerElement;
        this.headerSpacing.nativeElement.setAttribute('style', `height: ${space}px;`);
    }

    updateHeaderSearchBoxStatus(value: boolean) {
        this.headerState.showSearchBox = value;
        this.utilityService.currentHeaderState.next(this.headerState);
    }

    private _extractRouteData(route: any) {
        let currentRoute = route;
        while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
        }

        return currentRoute.data._value;
    }

    closeSmartBanner() {
        //shift header up
        const headerElement = this.headerElement.nativeElement as any;
        headerElement.setAttribute('style', 'top: 0px');
        this.storageService.set('smart_banner_shown', 'true', this.getSmartBannerExpiryDate());
        this.checkSmartBanner();
    }

    goBack() {
        this.pageNavigationService.back();
    }

    async isLocationInside(options: OutletType) {
        const { lat, lon, locality } = options;
        try {
            const response: any = await this.outletService.outletInside({ lat, lon, locality }).toPromise();
            return response.meta.code === 200 ? true : false;
        } catch (e) {}
    }

    async refreshOutlet(options: any) {
        this.reorderSavedAddresses(options.lat, options.lon);
        this.outletService.checkForOutletChange(options);
    }

    clearInput() {
        this.locationNotProper = false;
    }

    selectAddress(addrObj: any) {
        if (!addrObj.formatted_address) {
            this.placeSearch = '';
            return;
        }

        const formatted_address = addrObj.formatted_address;
        const { lat, lng } = this.addressService.getLatLng(addrObj);

        this.zone.run(() => {
            this.updateHeaderSearchBoxStatus(false);
            this.refreshOutlet({ lat, lon: lng, locality: formatted_address });
        });
    }

    selectSavedAddress(addrObj: CustomerAddress) {
        this.updateHeaderSearchBoxStatus(false);
        this.refreshOutlet({
            lat: addrObj.lat,
            lon: addrObj.lon,
            locality: addrObj.formatted_locality,
            address_type: addrObj.address_type,
            address_id: addrObj.id,
            service_type: 'DEL101'
        });
    }

    async reverseGeocode(latlng: LatLng) {
        this.locationApiService
            .reverseGeocode(latlng)
            .then((response: ReverseGeoCodeResponse) => {
                if (response && response.meta.code === 200) {
                    this.updateHeaderSearchBoxStatus(false);
                    const results = response.data.geocode_info;
                    const { location, formatted_address } = results[0];
                    this.refreshOutlet({
                        lat: location.lat,
                        lon: location.lng,
                        locality: formatted_address
                    });
                    this.gtmService.logEvent('web_analytics', {
                        event_action: 'address',
                        event_label: 'Reverse Geocode response received from get_reverse_geocode API',
                        value1: 'Mode: header'
                    });
                } else {
                    this.reverseGeocodeUsingGoogleService(latlng);
                }
            })
            .catch(e => {
                console.log(e);
                this.reverseGeocodeUsingGoogleService(latlng);
            });
    }

    reverseGeocodeUsingGoogleService(latlng: LatLng) {
        const _this = this;
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
            {
                location: latlng
            },
            function (results, status) {
                if (status === google.maps.GeocoderStatus.OK && results[0]) {
                    _this.updateHeaderSearchBoxStatus(false);
                    const { geometry, formatted_address } = results[0];
                    _this.refreshOutlet({
                        lat: geometry.location.lat(),
                        lon: geometry.location.lng(),
                        locality: formatted_address
                    });
                    _this.gtmService.logEvent('web_analytics', {
                        event_action: 'address',
                        event_label: 'Reverse Geocode response received from geocoder',
                        value1: 'Mode: header'
                    });
                }
            }
        );
    }

    autoDetectLocation() {
        this.locating = true;
        const startTime = Date.now();

        this.zone.run(() => {
            this.locationService
                .fetchLocation()
                .then((latlng: LatLng) => {
                    this.locationChangeInfo.show = false;
                    this.reverseGeocode(latlng);
                })
                .catch(error => {
                    const { message, code } = error;
                    this.locationChangeInfo.show = true;

                    if (message) {
                        this.locationChangeInfo.message = message;
                        this.toastrService.openSnackBar(message, 'OKAY');
                    }

                    if (code) {
                        this.gtmService.logWebAnalyticsEvent('address', 'Location fetch failed with code: ' + code);
                    }
                })
                .finally(() => {
                    this.locating = false;
                    this.updateHeaderSearchBoxStatus(false);
                    this.gtmService.endTimer(startTime, 'web_analytics', {
                        event_action: 'address',
                        event_label: 'Completion Time - Home Page'
                    });
                });
        });
    }

    isLoggedIn(): boolean {
        this.is_logged_in = this.storageService.get('session-id') && this.storageService.get('phone_no');
        if (this.is_logged_in) {
            this.customerName = this.storageService.get('name');
            return true;
        }
        return false;
    }

    getAuthDialogConfig() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.maxWidth = 420;
        dialogConfig.minWidth = 290;
        return dialogConfig;
    }

    showDefaultLoginModal(signInData) {
        if (this.isMobile) {
            const bottomRef = this.bottomSheet.open(MobileLoginWrapperComponent, {
                panelClass: 'bottom-sheet-wrapper',
                scrollStrategy: new NoopScrollStrategy()
            });

            bottomRef.afterDismissed().subscribe(() => {
                this.authenticationService.resetStep();
                this.router.navigate([], { replaceUrl: true });
            });
        } else {
            const dialogConfig = this.getAuthDialogConfig();
            dialogConfig.data = signInData;
            dialogConfig.panelClass = 'signin-desktop-dialog';
            dialogConfig.scrollStrategy = new NoopScrollStrategy(); // Doesn't block scrolling, used bcoz, when we open mat dialog it sets window.scrollY 0, which was breaking header styling

            const dialogRef = this.dialog.open(DesktopLoginWrapperComponent, dialogConfig);
            dialogRef.afterClosed().subscribe(data => {
                // Checks if User closed the dialogue
                // data: { authentication: Boolean }
                this.authenticationService.resetStep();
                if (!data?.authentication) {
                    this.router.navigate([], { replaceUrl: true });
                }
            });
        }
    }

    showSignInModal(signInData = ''): void {
        this.showDefaultLoginModal(signInData);
    }

    updateLocation(position: GeolocationPosition) {
        if (!position) {
            return;
        }

        this.showLocationChange = false;

        this.googleMapService.loadGoogleMap(this.updateUserLocation.bind(this, position)).catch(() => {
            this.toastrService.openSnackBar('Unable to update location. Please type in the search bar.', 'OKAY!');
        });
    }

    updateUserLocation(position: GeolocationPosition) {
        this.reverseGeocode({
            lat: position.coords.latitude,
            lng: position.coords.longitude
        });
    }

    hideUpdateLocation() {
        this.showLocationChange = false;
        this.sessionStorage.set('askForLocationUpdate', false);
    }

    checkCurrentLocation() {
        if (location.pathname !== '/' && !location.pathname.includes('category') && !location.pathname.includes('menu')) {
            return;
        }

        const _this = this;
        if (!_this.storageService.get('longitude') || !_this.storageService.get('latitude')) {
            return;
        }

        if (_this.sessionStorage.get('askForLocationUpdate') === false) {
            return;
        }
        let distance: number;

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                function (position) {
                    _this.locationChangeInfo.show = false;
                    _this.position = position;
                    const lat1 = Number(_this.storageService.get('latitude') || null);
                    const lon1 = Number(_this.storageService.get('longitude') || null);
                    const lat2 = position.coords.latitude;
                    const lon2 = position.coords.longitude;

                    if (lat1 && lon1 && lat2 && lon2) {
                        distance = _this.outletService.getDistanceFromLatLonInMeter(lat1, lon1, lat2, lon2);
                        if (distance > 1500) {
                            _this.showLocationChange = true;
                        } else {
                            _this.sessionStorage.set('askForLocationUpdate', false);
                        }
                    }
                },
                function (error) {
                    console.log(error);
                }
            );
        } else {
            _this.locationChangeInfo.show = true;
            _this.locationChangeInfo.message =
                'Sorry, we are unable to fetch your current location. Please type your location in the search box above.';
        }
    }

    editLocality() {
        // emptying search input
        this.placeSearch = '';
        this.clearInput();
        this.updateHeaderSearchBoxStatus(true);
        this.googleMapService.loadGoogleMap().catch(err => console.log(err));
    }

    hideSearchBox() {
        this.updateHeaderSearchBoxStatus(false);
    }

    logOut() {
        this.customerService.logOut();
    }

    navigateTo(url: string) {
        this.router.navigateByUrl(url, {
            state: {
                referrer: 'Header'
            }
        });
    }

    reorderSavedAddresses(lat, lon) {
        this.addresses.sort((addressA, addressB) => {
            const distanceA = this.outletService.getDistanceFromLatLonInMeter(addressA.lat, addressA.lon, lat, lon);
            const distanceB = this.outletService.getDistanceFromLatLonInMeter(addressB.lat, addressB.lon, lat, lon);
            return distanceA - distanceB;
        });
    }

    shouldApplyScrollEffects(): boolean {
        return this.headerState && this.headerState.isMainPage && !this.isScrolled;
    }

    calculateScrollPercentage(scrollTop: number, scrollHeight: number): number {
        if (scrollHeight <= 0) return 0;
        return Math.min(scrollTop / scrollHeight, 1);
    }

    updateHeaderStyles(scrollPercentage: number): void {
        this.backgroundColor = `rgba(255, 255, 255, ${scrollPercentage})`;
        this.textColor = this.interpolateColor(scrollPercentage);
        this.boxShadow =
            scrollPercentage === 1 ? this.dynamicStyles.finalStyle.boxShadow : this.dynamicStyles.initialStyle.boxShadow;
    }

    interpolateColor(scrollPercentage: number): string {
        const startColor = { r: 255, g: 255, b: 255 };
        const endColor = { r: 0, g: 0, b: 0 };
        const currentColor = {
            r: Math.round(startColor.r + scrollPercentage * (endColor.r - startColor.r)),
            g: Math.round(startColor.g + scrollPercentage * (endColor.g - startColor.g)),
            b: Math.round(startColor.b + scrollPercentage * (endColor.b - startColor.b))
        };
        return `rgb(${currentColor.r}, ${currentColor.g}, ${currentColor.b})`;
    }

    updateHeaderState(urlWithoutParam: string, fullUrl: string, routeData: any): void {
        this.headerState.isMainPage = urlWithoutParam === '/';
        this.headerState.showEditButton = ['/category', '/', '/menu'].includes(urlWithoutParam);
        this.headerState.showSearchBox = false;
        this.headerState.isCartPage = fullUrl.includes('/cart');
        this.headerState.isOffersPage = urlWithoutParam === '/offers';
        this.headerState.isCreditsPage = urlWithoutParam === '/credits';
        this.headerState.isMenuPage = fullUrl.includes('/menu');
        this.headerState.isBrandingPage = fullUrl.includes('/membership');
        this.headerState.isAccountPage = fullUrl.includes('/account');
        this.headerState.currentUrl = routeData.pageTitle;
        this.showMobileHeader = routeData.hideHeader === undefined && this.isMobile;
        this.showCartDropDown = routeData.showCart !== undefined;
    }

    setInitialHeaderStyles(): void {
        if (!this.shouldApplyScrollEffects()) {
            this.backgroundColor = this.dynamicStyles.finalStyle.backgroundColor;
            this.textColor = this.dynamicStyles.finalStyle.textColor;
            this.boxShadow = this.dynamicStyles.finalStyle.boxShadow;
        }
    }

    handlePostNavigationActions(urlWithoutParam: string): void {
        this.utilityService.currentHeaderState.next(this.headerState);
        this.seo.updateTag(urlWithoutParam);
        this.checkSmartBanner();
        this.fetchCreditBalance();
    }

    handlePslaNotification(data: any): void {
        this.headerNotification = data;
        let backgroundColor, boxShadow, textColor;

        if (Object.keys(data).length && !data.promotionalBannerPresent) {
            this.isScrolled = true;
            ({ backgroundColor, boxShadow, textColor } = this.dynamicStyles.finalStyle);
        } else if (
            window.scrollY >= this.SCROLL_THRESHOLD_HEIGHT ||
            document.documentElement.scrollTop >= this.SCROLL_THRESHOLD_HEIGHT
        ) {
            this.isScrolled = false;
            ({ backgroundColor, boxShadow, textColor } = this.dynamicStyles.finalStyle);
        } else {
            this.isScrolled = false;
            ({ backgroundColor, boxShadow, textColor } = this.dynamicStyles.initialStyle);
        }

        this.backgroundColor = backgroundColor;
        this.boxShadow = boxShadow;
        this.textColor = textColor;
    }

    scrollToDownloadBanner() {
        const el = document.getElementById('downloadBanner');
        if (el) {
            el.scrollIntoView({ block: 'end', behavior: 'smooth' }); //added at end, bcoz, start or center, first time not working properly
        }
    }
}
